import React, { useState, useEffect, useRef } from 'react';
import { X, Scale, Ruler, ArrowLeft, Trash2, Boxes, ChevronDown, Check } from 'lucide-react';
import * as servicesApi from '../services/servicesApi';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import FloatingField from './FloatingField';
import ModalShell from './ModalShell';
import { ModalListSkeleton } from './Skeleton';
import { haptic } from '../utils/vibration';

const normalizeOrderServiceType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  const aliases = {
    laundry: 'washing',
    washing: 'washing',
    'dry-cleaning': 'cleaning',
    'dry cleaning': 'cleaning',
    drycleaning: 'cleaning',
    cleaning: 'cleaning'
  };
  return aliases[normalized] || normalized;
};

const getItemDisplayNumber = (item, itemIndex) => (
  item?.sort_order
    ? String(item.sort_order)
    : String((itemIndex ?? 0) + 1)
);

const normalizeMeasurementValue = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeMeasurementInput = (value) => String(value ?? '').replace(',', '.');

const isValidMeasurementPrecision = (value) => {
  const normalized = normalizeMeasurementInput(value).trim();
  if (!normalized) return true;
  return /^\d+(?:\.\d{0,1})?$/.test(normalized);
};

const validateMeasurementInput = (value, label) => {
  const normalized = normalizeMeasurementInput(value).trim();
  if (!normalized) {
    return '';
  }

  if (!isValidMeasurementPrecision(normalized)) {
    return `${label} можно указывать только с шагом 0.1`;
  }

  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return `${label} должен быть больше 0`;
  }

  if (parsed > 1000) {
    return `${label} не может быть больше 1000`;
  }

  return '';
};

const validateDimensionInput = (value, label) => {
  const normalized = normalizeMeasurementInput(value).trim();
  if (!normalized) {
    return '';
  }

  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return `${label} должна быть больше 0`;
  }

  if (parsed > 1000) {
    return `${label} не может быть больше 1000`;
  }

  return '';
};

const calculateAreaFromDimensions = (widthVal, lengthVal) => {
  const normW = normalizeMeasurementInput(widthVal).trim();
  const normL = normalizeMeasurementInput(lengthVal).trim();
  if (!normW || !normL) return '';
  const w = Number.parseFloat(normW);
  const l = Number.parseFloat(normL);
  if (!Number.isFinite(w) || w <= 0 || !Number.isFinite(l) || l <= 0) return '';
  const calculated = w * l;
  const rounded = Math.max(0.1, Math.round(calculated * 10) / 10);
  return String(Number(rounded.toFixed(1)));
};

const getSavedAreaInputMode = () => {
  try {
    const saved = localStorage.getItem('bw_item_area_input_mode');
    if (saved === 'area' || saved === 'dimensions') return saved;
  } catch {
    // ignore
  }
  return 'dimensions';
};

const getSortedAdditionalIds = (items = []) => (
  items
    .map((service) => Number(service?.id))
    .filter(Number.isFinite)
    .sort((left, right) => left - right)
);

const ItemDetailModal = ({
  open,
  onClose,
  item,
  itemIndex,
  itemDisplayNumber,
  serviceType,
  clientType,
  onItemUpdate,
  canEdit = true,
  canEditMeasurements = true,
  canEditStorageCell = false,
  storageCells = [],
  storageCell = null,
  orderStatus = '',
  priceAt = null,
  priceCoefficient = 1
}) => {
  const [additionalServices, setAdditionalServices] = useState({ groups: [], ungrouped: [] });
  const [selectedAdditional, setSelectedAdditional] = useState([]);
  const [weight, setWeight] = useState('');
  const [area, setArea] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [selectedStorageCellId, setSelectedStorageCellId] = useState('');
  const [cellDropdownOpen, setCellDropdownOpen] = useState(false);
  const [areaInputMode, setAreaInputMode] = useState('dimensions'); // 'dimensions' | 'area'
  const [measurementEditorOpen, setMeasurementEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('main'); // 'main' | 'services'
  const lengthInputRef = useRef(null);
  const cellPickerRef = useRef(null);
  const { showToast } = useToast();
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!cellDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (cellPickerRef.current && !cellPickerRef.current.contains(e.target)) {
        setCellDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cellDropdownOpen]);

  const isWeightUnit = (unit) => unit === 'кг';
  const isAreaUnit = (unit) => ['м²', 'м2', 'кв.м'].includes(unit);
  const adjustedRubPrice = (price) => Number(price || 0) * Number(priceCoefficient || 1);
  const adjustedItemRegularPrice = adjustedRubPrice(item?.regular_price ?? item?.price ?? 0);
  const adjustedItemPromotionPrice = item?.promotion_price != null ? adjustedRubPrice(item.promotion_price) : null;
  const normalizedServiceType = normalizeOrderServiceType(serviceType);

  useEffect(() => {
    if (open && item) {
      setWeight(item.weight != null ? String(item.weight) : '');
      const itemAreaStr = item.area != null ? String(item.area) : '';
      const itemWidthStr = item.width != null ? String(item.width) : '';
      const itemLengthStr = item.length != null ? String(item.length) : '';

      setWidth(itemWidthStr);
      setLength(itemLengthStr);
      setArea(itemAreaStr);

      const currentCell = storageCell || item.storage_cell || null;
      setSelectedStorageCellId(currentCell?.id ? String(currentCell.id) : (item?.storage_cell_id ? String(item.storage_cell_id) : ''));
      setCellDropdownOpen(false);

      const hasDims = (Number(item.width) > 0 || Number(item.length) > 0);
      const hasOnlyArea = Number(item.area) > 0 && !hasDims;
      let initialMode;
      if (hasDims) {
        initialMode = 'dimensions';
      } else if (hasOnlyArea) {
        initialMode = 'area';
      } else {
        initialMode = getSavedAreaInputMode();
      }
      setAreaInputMode(initialMode);

      setSelectedAdditional(item.additionalServices || []);
      setMeasurementEditorOpen(false);
      setSaving(false);
      setView('main');
      loadAdditionalServices();
    }
  }, [open, item, storageCell, priceAt]);

  const loadAdditionalServices = async () => {
    try {
      setLoading(true);
      const extraType = normalizedServiceType === 'cleaning' ? 'extra_cleaning' : 'extra_washing';
      const servicesData = await servicesApi.getServices({
        active_only: true,
        client_type: clientType,
        service_type: extraType,
        price_at: priceAt
      });

      const groupsMap = {};
      const ungrouped = [];
      for (const svc of servicesData) {
        if (svc.group_id && svc.group_name) {
          if (!groupsMap[svc.group_id]) groupsMap[svc.group_id] = { id: svc.group_id, name: svc.group_name, services: [] };
          groupsMap[svc.group_id].services.push(svc);
        } else {
          ungrouped.push(svc);
        }
      }
      setSelectedAdditional(prev => prev.map(selected => servicesData.find(svc => svc.id === selected.id) || selected));
      setAdditionalServices({ groups: Object.values(groupsMap), ungrouped });
    } catch (err) {
      console.error('Ошибка загрузки доп. услуг:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdditionalService = (service) => {
    const exists = selectedAdditional.find(s => s.id === service.id);
    haptic(exists ? 'tap' : 'success');
    setSelectedAdditional(prev => {
      const serviceExists = prev.find(s => s.id === service.id);
      return serviceExists ? prev.filter(s => s.id !== service.id) : [...prev, { ...service, quantity: 1 }];
    });
  };

  const removeAdditionalService = (serviceId) => {
    haptic('tap');
    setSelectedAdditional(prev => prev.filter(s => s.id !== serviceId));
  };

  const calculateCost = () => {
    if (!item) return { total: 0, known: true };
    const basePrice = adjustedRubPrice(item.price || 0);
    let known = true;
    if (isWeightUnit(item.unit) && !weight) known = false;
    if (isAreaUnit(item.unit) && !area) known = false;

    let baseCost = basePrice * (item.quantity || 1);
    if (isWeightUnit(item.unit) && weight) baseCost = basePrice * parseFloat(weight);
    if (isAreaUnit(item.unit) && area) baseCost = basePrice * parseFloat(area);

    let additionalCost = 0;
    for (const add of selectedAdditional) {
      const price = add.current_price || add.base_price || 0;
      const unitType = add.current_unit_type || 'rub';
      additionalCost += unitType === 'percent' ? baseCost * (price / 100) : price;
    }
    return { total: baseCost + additionalCost, known };
  };

  if (!item) return null;

  const cost = calculateCost();
  const itemNumber = itemDisplayNumber || getItemDisplayNumber(item, itemIndex);
  const allServices = [...(additionalServices.groups?.flatMap(g => g.services) || []), ...(additionalServices.ungrouped || [])];
  const measurementLabel = isWeightUnit(item.unit) ? 'Вес' : 'Площадь';
  const measurementIcon = isWeightUnit(item.unit) ? Scale : Ruler;
  const MeasurementIcon = measurementIcon;
  const measurementUnit = isWeightUnit(item.unit) ? 'кг' : 'м²';
  const isWeight = isWeightUnit(item.unit);
  const isArea = isAreaUnit(item.unit);
  const isDimensions = isArea && areaInputMode === 'dimensions';

  let measurementError = '';
  if (isWeight) {
    measurementError = validateMeasurementInput(weight, 'Вес');
  } else if (isArea) {
    if (areaInputMode === 'dimensions') {
      const widthErr = validateDimensionInput(width, 'Ширина');
      const lengthErr = validateDimensionInput(length, 'Длина');
      if (widthErr) {
        measurementError = widthErr;
      } else if (lengthErr) {
        measurementError = lengthErr;
      } else if (width && !length) {
        measurementError = 'Укажите длину';
      } else if (!width && length) {
        measurementError = 'Укажите ширину';
      } else if (area) {
        measurementError = validateMeasurementInput(area, 'Площадь');
      }
    } else {
      measurementError = validateMeasurementInput(area, 'Площадь');
    }
  }

  const measurementValue = isWeight ? weight : area;
  const measurementDisplayValue = isWeight
    ? (weight ? `${weight} кг` : 'Не указан')
    : (area
        ? (width && length
            ? `${area} м² (${width} × ${length} м)`
            : `${area} м²`)
        : 'Не указана');

  const initialWeight = normalizeMeasurementValue(item.weight);
  const initialArea = normalizeMeasurementValue(item.area);
  const initialWidth = normalizeMeasurementValue(item.width);
  const initialLength = normalizeMeasurementValue(item.length);

  const currentWeight = isWeight ? normalizeMeasurementValue(weight) : null;
  const currentArea = isArea ? normalizeMeasurementValue(area) : null;
  const currentWidth = isDimensions ? normalizeMeasurementValue(width) : null;
  const currentLength = isDimensions ? normalizeMeasurementValue(length) : null;

  const initialStorageCellId = storageCell?.id ? String(storageCell.id) : (item?.storage_cell_id ? String(item.storage_cell_id) : '');
  const hasStorageCellChange = Boolean(canEditStorageCell && selectedStorageCellId !== initialStorageCellId);

  const initialAdditionalIds = getSortedAdditionalIds(item.additionalServices || []);
  const currentAdditionalIds = getSortedAdditionalIds(selectedAdditional);
  const hasAdditionalChanges = (
    initialAdditionalIds.length !== currentAdditionalIds.length
    || initialAdditionalIds.some((id, index) => id !== currentAdditionalIds[index])
  );
  const hasMeasurementChanges = canEditMeasurements && (
    initialWeight !== currentWeight
    || initialArea !== currentArea
    || initialWidth !== currentWidth
    || initialLength !== currentLength
  );
  const hasPendingChanges = hasMeasurementChanges || hasAdditionalChanges || hasStorageCellChange;
  const canSave = hasPendingChanges && (!canEditMeasurements || !measurementError) && !saving;

  const handleMeasurementChange = (rawValue) => {
    const nextValue = normalizeMeasurementInput(rawValue);
    if (isWeight) {
      setWeight(nextValue);
      return;
    }
    setArea(nextValue);
  };

  const handleAreaModeChange = (newMode) => {
    haptic('selection');
    setAreaInputMode(newMode);
    try {
      localStorage.setItem('bw_item_area_input_mode', newMode);
    } catch {
      // ignore
    }
    if (newMode === 'dimensions') {
      if (width && length) {
        const nextArea = calculateAreaFromDimensions(width, length);
        if (nextArea) setArea(nextArea);
      }
    }
  };

  const handleWidthChange = (rawValue) => {
    const nextVal = normalizeMeasurementInput(rawValue);
    setWidth(nextVal);
    const nextArea = calculateAreaFromDimensions(nextVal, length);
    setArea(nextArea);
  };

  const handleLengthChange = (rawValue) => {
    const nextVal = normalizeMeasurementInput(rawValue);
    setLength(nextVal);
    const nextArea = calculateAreaFromDimensions(width, nextVal);
    setArea(nextArea);
  };

  const handleSave = async () => {
    if (!item || (canEditMeasurements && measurementError) || !canEdit) return;
    haptic('success');
    setSaving(true);
    let shouldClose = false;
    try {
      const updatedItem = {
        ...item,
        additionalServices: selectedAdditional,
        ...(canEditMeasurements ? {
          weight: isWeight && weight ? parseFloat(weight) : null,
          area: isArea && area ? parseFloat(area) : null,
          length: isDimensions && length ? parseFloat(length) : null,
          width: isDimensions && width ? parseFloat(width) : null
        } : {}),
        ...(hasStorageCellChange ? {
          storage_cell_id: selectedStorageCellId ? Number(selectedStorageCellId) : null
        } : {})
      };
      const result = await onItemUpdate(updatedItem);
      shouldClose = result !== false;
    } finally {
      setSaving(false);
    }
    if (shouldClose) onClose();
  };

  return (
    <ModalShell
      open={open}
      onClose={() => {
        haptic('close');
        onClose();
      }}
      variant="sheet"
      expandable={true}
      panelClassName="modal-bottom-sheet flex w-full flex-col overflow-hidden sm:max-w-md"
    >
        {/* Заголовок */}
        <div className="shrink-0 rounded-t-2xl border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900 sm:rounded-t-2xl">
          <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {view === 'services' && (
                <button onClick={() => { haptic('close'); setView('main'); }} className="cart-btn w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0">
                <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            )}
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-wide text-blue-600 dark:text-blue-300">
                Предмет {itemNumber}
              </div>
              <h3 className="truncate text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                {item.name}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {view === 'services'
                  ? 'Доп. услуги'
                  : adjustedItemPromotionPrice !== null
                    ? `${adjustedItemPromotionPrice.toFixed(0)} ₽ / ${item.unit} вместо ${adjustedItemRegularPrice.toFixed(0)} ₽`
                    : `${adjustedRubPrice(item.price).toFixed(0)} ₽ / ${item.unit}`}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                {clientType === 'b2b' ? 'Юр. лица' : 'Физ. лица'}
                {Number(priceCoefficient || 1) !== 1 ? ` · коэф. ×${priceCoefficient}` : ''}
              </p>
            </div>
          </div>
          <button onClick={() => { haptic('close'); onClose(); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg ml-2 shrink-0">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
          </div>
        </div>

        {/* Основной вид */}
        {view === 'main' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
            {/* Вес / Площадь */}
            {item.unit !== 'шт' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!canEdit) return;
                    if (!canEditMeasurements) {
                      haptic('warning');
                      showToast('error', 'Изменение площади и размеров на этом этапе доступно только диспетчеру');
                      return;
                    }
                    haptic(measurementEditorOpen ? 'close' : 'open');
                    setMeasurementEditorOpen((prev) => !prev);
                  }}
                  disabled={!canEdit}
                  className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <MeasurementIcon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{measurementLabel}</span>
                  </span>
                  <span className={`ml-3 text-sm ${measurementValue ? 'font-medium text-gray-900 dark:text-gray-100' : 'font-medium text-red-500 dark:text-red-400'}`}>
                    {measurementDisplayValue}
                  </span>
                </button>

                {measurementEditorOpen && (
                  <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-2.5 dark:border-blue-900/50 dark:bg-blue-950/20">
                    {/* Компактный переключатель и статус расчёта */}
                    {isArea && (
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
                          {isDimensions ? (
                            area ? (
                              <span>
                                Площадь:{' '}
                                <strong className="font-semibold text-blue-600 dark:text-blue-400">
                                  {area} м²
                                </strong>
                              </span>
                            ) : (
                              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                Размеры (м)
                              </span>
                            )
                          ) : (
                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                              Площадь (м²)
                            </span>
                          )}
                        </div>
                        <div className="inline-flex shrink-0 rounded-lg bg-gray-200/70 p-0.5 dark:bg-gray-800">
                          <button
                            type="button"
                            onClick={() => handleAreaModeChange('dimensions')}
                            className={`rounded-md px-2.5 py-0.5 text-[11px] transition-all ${
                              areaInputMode === 'dimensions'
                                ? 'bg-white font-semibold text-blue-600 shadow-xs dark:bg-gray-700 dark:text-blue-300'
                                : 'font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                          >
                            Размеры
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAreaModeChange('area')}
                            className={`rounded-md px-2.5 py-0.5 text-[11px] transition-all ${
                              areaInputMode === 'area'
                                ? 'bg-white font-semibold text-blue-600 shadow-xs dark:bg-gray-700 dark:text-blue-300'
                                : 'font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                          >
                            Площадь
                          </button>
                        </div>
                      </div>
                    )}

                    {isDimensions ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-1.5">
                          <FloatingField label="Ширина" className="flex-1 min-w-0" disabled={!canEdit || !canEditMeasurements}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="1000"
                              value={width}
                              onChange={(e) => handleWidthChange(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  lengthInputRef.current?.focus();
                                }
                              }}
                              enterKeyHint="next"
                              autoFocus
                              disabled={!canEdit || !canEditMeasurements}
                              className="w-full rounded-lg bg-white px-3 py-2 text-sm placeholder:text-[11px] placeholder:font-normal focus:outline-none dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                              placeholder="Ширина"
                            />
                          </FloatingField>
                          <span className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                            м
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <FloatingField label="Длина" className="flex-1 min-w-0" disabled={!canEdit || !canEditMeasurements}>
                            <input
                              ref={lengthInputRef}
                              type="number"
                              step="0.01"
                              min="0"
                              max="1000"
                              value={length}
                              onChange={(e) => handleLengthChange(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !measurementError && area) {
                                  e.currentTarget.blur();
                                  setMeasurementEditorOpen(false);
                                }
                              }}
                              enterKeyHint="done"
                              disabled={!canEdit || !canEditMeasurements}
                              className="w-full rounded-lg bg-white px-3 py-2 text-sm placeholder:text-[11px] placeholder:font-normal focus:outline-none dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                              placeholder="Длина"
                            />
                          </FloatingField>
                          <span className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                            м
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <FloatingField label={measurementLabel} className="w-36 max-w-full" disabled={!canEdit || !canEditMeasurements}>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="1000"
                            value={measurementValue}
                            onChange={(e) => handleMeasurementChange(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !measurementError) {
                                e.currentTarget.blur();
                                setMeasurementEditorOpen(false);
                              }
                            }}
                            enterKeyHint="done"
                            autoFocus
                            disabled={!canEdit || !canEditMeasurements}
                            className="w-full rounded-lg bg-white px-3 py-2 text-sm placeholder:text-[11px] placeholder:font-normal focus:outline-none dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                            placeholder={`Введите ${measurementLabel.toLowerCase()}`}
                          />
                        </FloatingField>
                        <span className="shrink-0 rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
                          {measurementUnit}
                        </span>
                      </div>
                    )}

                    {measurementError && (
                      <p className="mt-2 text-xs font-medium text-red-500 dark:text-red-400">
                        {measurementError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Ячейка хранения */}
            {(item?.processing_route === 'cleaning' || storageCell || canEditStorageCell) && (() => {
              const selectedCell = storageCells.find((c) => String(c.id) === String(selectedStorageCellId))
                || (storageCell && String(storageCell.id) === String(selectedStorageCellId) ? storageCell : null);
              return (
                <div className="relative" ref={cellPickerRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canEdit || !canEditStorageCell) return;
                      haptic('selection');
                      setCellDropdownOpen((prev) => !prev);
                    }}
                    disabled={!canEdit || !canEditStorageCell}
                    className={`flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left transition-colors ${
                      canEditStorageCell ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                    } dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Boxes className="h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Ячейка хранения</span>
                    </span>
                    <div className="flex items-center gap-1.5">
                      {selectedCell ? (
                        <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-700 dark:bg-purple-950/50 dark:text-purple-300">
                          {selectedCell.name}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Не указана
                        </span>
                      )}
                      {canEditStorageCell && (
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${cellDropdownOpen ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </button>

                  {cellDropdownOpen && canEditStorageCell && (
                    <div className="absolute left-0 right-0 top-full mt-1.5 z-30 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                      {storageCells.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
                          Нет активных ячеек
                        </div>
                      ) : (
                        storageCells.map((cell) => {
                          const isSelected = selectedStorageCellId === String(cell.id);
                          return (
                            <button
                              key={cell.id}
                              type="button"
                              onClick={() => {
                                haptic('selection');
                                setSelectedStorageCellId(String(cell.id));
                                setCellDropdownOpen(false);
                              }}
                              className={`flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm transition-colors ${
                                isSelected
                                  ? 'bg-purple-50 font-semibold text-purple-700 dark:bg-purple-950/40 dark:text-purple-300'
                                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-750'
                              }`}
                            >
                              <span className="truncate pr-2">{cell.name}</span>
                              {isSelected && <Check className="h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Доп. услуги — кнопка перехода */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Доп. услуги
                {selectedAdditional.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs rounded-full">
                    {selectedAdditional.length}
                  </span>
                )}
              </span>
              <button
                onClick={() => { if (!canEdit) return; haptic('open'); setView('services'); }}
                disabled={!canEdit}
                className="h-8 rounded-lg border-2 border-blue-500 px-3 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-900/20"
              >
                Добавить
              </button>
            </div>

            {/* Выбранные доп. услуги */}
            {selectedAdditional.length > 0 && (
              <div className="space-y-1">
                {selectedAdditional.map(add => {
                  const price = add.current_price || add.base_price || 0;
                  const unitType = add.current_unit_type || 'rub';
                  return (
                    <div key={add.id} className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/10 rounded-lg text-sm">
                      <span className="text-gray-900 dark:text-gray-100 flex-1 truncate">{add.name}</span>
                      <div className="ml-2 flex items-center gap-2 shrink-0">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {unitType === 'percent' ? `${price}%` : `${price} ₽`}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAdditionalService(add.id)}
                          disabled={!canEdit}
                          className="cart-btn flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-600 transition-colors hover:bg-red-200 disabled:opacity-50 dark:bg-red-900/30 dark:text-red-400"
                          title="Удалить доп. услугу"
                          aria-label={`Удалить доп. услугу ${add.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Итого */}
            <div className="flex items-center justify-between rounded-xl bg-gray-50 p-3 dark:bg-gray-800">
              <span className="text-sm text-gray-600 dark:text-gray-400">Стоимость:</span>
              <span className={`text-lg font-bold ${cost.known ? 'text-gray-900 dark:text-gray-100' : 'text-orange-500'}`}>
                {cost.known ? `${cost.total.toFixed(0)} ₽` : '?'}
              </span>
            </div>

            <button
              onClick={handleSave}
              disabled={!canSave || !canEdit}
              className={`w-full rounded-xl py-2.5 text-sm font-medium text-white transition-colors ${
                canSave && canEdit
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'cursor-not-allowed bg-blue-300 dark:bg-blue-900/50'
              }`}
            >
              {saving ? 'Сохраняю...' : 'Сохранить'}
            </button>
          </div>
        )}

        {/* Вид выбора доп. услуг */}
        {view === 'services' && (
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4">
                <ModalListSkeleton count={4} />
              </div>
            ) : allServices.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">Нет доступных услуг</div>
            ) : (
              <div className="p-4 space-y-3">
                {additionalServices.groups?.map(group => (
                  <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {group.name}
                    </div>
                    {group.services.map(svc => {
                      const selected = !!selectedAdditional.find(s => s.id === svc.id);
                      const price = svc.current_price || svc.base_price || 0;
                      const unitType = svc.current_unit_type || 'rub';
                      return (
                        <div
                          key={svc.id}
                          className={`flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
                            selected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0 mr-3">
                            <div className="text-sm text-gray-900 dark:text-gray-100">{svc.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {unitType === 'percent' ? `${price}% от суммы` : `${price} ₽`}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleAdditionalService(svc)}
                            disabled={!canEdit}
                            className="toggle-switch shrink-0"
                            style={{
                              '--toggle-bg': selected ? 'rgb(59 130 246)' : 'rgb(209 213 219)',
                              '--toggle-height': '22px',
                              '--toggle-width': '42px',
                              '--knob-x': selected ? '20px' : '2px'
                            }}
                          >
                            <span className="toggle-switch-knob" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {additionalServices.ungrouped?.length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    {additionalServices.ungrouped.map(svc => {
                      const selected = !!selectedAdditional.find(s => s.id === svc.id);
                      const price = svc.current_price || svc.base_price || 0;
                      const unitType = svc.current_unit_type || 'rub';
                      return (
                        <div
                          key={svc.id}
                          className={`flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
                            selected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0 mr-3">
                            <div className="text-sm text-gray-900 dark:text-gray-100">{svc.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {unitType === 'percent' ? `${price}% от суммы` : `${price} ₽`}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleAdditionalService(svc)}
                            disabled={!canEdit}
                            className="toggle-switch shrink-0"
                            style={{
                              '--toggle-bg': selected ? 'rgb(59 130 246)' : 'rgb(209 213 219)',
                              '--toggle-height': '22px',
                              '--toggle-width': '42px',
                              '--knob-x': selected ? '20px' : '2px'
                            }}
                          >
                            <span className="toggle-switch-knob" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
    </ModalShell>
  );
};

export {
  calculateAreaFromDimensions,
  validateDimensionInput,
  validateMeasurementInput,
  getSavedAreaInputMode
};

export default ItemDetailModal;
